import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Mail, MessageSquare, Phone, Star } from 'lucide-react';
import {
  formatCurrency,
  type BookingStageOption,
  type EventBooking,
  type EventVendorMetadataInput,
  formatPhone,
  buildWhatsAppUrl,
} from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { TabNav } from '../common/TabNav';
import { StatusBadge } from '../common/EntityList';
import { SettingListSelect } from '../floor-plan/SettingListSelect';
import { UserSearchableSelect } from '../users/UserSearchableSelect';
import { EntityAttachmentSection } from '../attachments/EntityAttachmentSection';
import type { RequestFilesSuggestion } from '../files/RequestFilesModal';
import { VendorMessagingControl } from '../vendors/VendorMessagingControl';
import { VendorPortalAccessPanel } from './VendorPortalAccessPanel';
import { BookingTimelineAction } from './BookingTimelineAction';
import { useDialog } from '../../context/DialogContext';
import { usePatchBooking } from '../../hooks/useBookings';
import { extractApiErrorMessage } from '../../utils/apiError';
import { shortDay } from '../needs/needDisplay';
import { BOOKING_KIND_COLORS, coiWarning, phoneHref } from './bookingDisplay';
import { WhatsAppIcon } from '../common/WhatsAppIcon';

export const BOOKING_SHEET_TABS = ['details', 'needs', 'contacts', 'files', 'portal'] as const;
export type BookingSheetTab = (typeof BOOKING_SHEET_TABS)[number];

export function isBookingSheetTab(value: string | null): value is BookingSheetTab {
  return !!value && (BOOKING_SHEET_TABS as readonly string[]).includes(value);
}

export interface BookingSheetProps {
  /** The booking open; null closes the sheet. */
  booking: EventBooking | null;
  /** The event the booking is on — the Portal tab shares its folders and plans. */
  eventId: string;
  stages: readonly BookingStageOption[];
  tab: BookingSheetTab;
  onTabChange: (tab: BookingSheetTab) => void;
  onClose: () => void;
  /** Opens one of the booking's needs on the Plan view. */
  onOpenNeed: (needId: string) => void;
}

const money = (n: number) => formatCurrency(n);

/**
 * What happens after the decision, for one of the event's vendors: its stage
 * (booked → contract signed → confirmed, the office's list), the day-of times
 * and contact, the contract and insurance dates, notes, messaging, the needs
 * booked with it, its people, its files and what it sees in the vendor portal. The tab lives in the address
 * (`?booking=<linkId>&tab=`).
 */
export function BookingSheet({ booking, onClose, ...rest }: BookingSheetProps) {
  const dirty = useRef(false);
  const { confirm } = useDialog();
  const close = useCallback(async () => {
    if (
      dirty.current &&
      !(await confirm({
        title: 'Unsaved changes',
        message: 'You have unsaved changes. Discard them?',
        confirmLabel: 'Discard',
        variant: 'warning',
      }))
    ) {
      return;
    }
    dirty.current = false;
    onClose();
  }, [confirm, onClose]);

  return (
    <EntityModal
      open={booking !== null}
      onClose={() => void close()}
      title={booking?.vendorName ?? 'Vendor'}
      size="lg"
      // One height for every tab, as the need sheet does: sized to its content,
      // the sheet jumped as the tabs changed under the pointer.
      fullHeight
    >
      {booking && (
        <BookingSheetBody
          key={booking.linkId}
          booking={booking}
          onUnsavedChange={(d) => (dirty.current = d)}
          {...rest}
        />
      )}
    </EntityModal>
  );
}

/**
 * Who a vendor's files are asked of: the business's own address, then each of
 * its contacts who has one — the people the Contacts tab lists.
 */
export function bookingFileRecipients(booking: EventBooking): RequestFilesSuggestion[] {
  return [
    ...(booking.vendorEmail
      ? [{ email: booking.vendorEmail, name: booking.vendorName, description: 'Vendor' }]
      : []),
    ...booking.contacts.flatMap((contact) =>
      contact.email
        ? [{ email: contact.email, name: contact.name, description: contact.role ?? 'Contact' }]
        : [],
    ),
  ];
}

function BookingSheetBody({
  booking,
  eventId,
  stages,
  tab,
  onTabChange,
  onOpenNeed,
  onUnsavedChange,
}: Omit<BookingSheetProps, 'booking' | 'onClose'> & {
  booking: EventBooking;
  onUnsavedChange: (dirty: boolean) => void;
}) {
  const patch = usePatchBooking();
  const { alert } = useDialog();
  const warning = coiWarning(booking);

  const save = async (changes: EventVendorMetadataInput): Promise<boolean> => {
    try {
      await patch.mutateAsync({ linkId: booking.linkId, patch: changes });
      return true;
    } catch (err) {
      await alert({
        title: 'Not saved',
        message: extractApiErrorMessage(err, 'The vendor’s details could not be saved.'),
        variant: 'error',
      });
      return false;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
        {booking.stage && (
          <StatusBadge
            label={booking.stage.label}
            color={BOOKING_KIND_COLORS[booking.stage.kind]}
          />
        )}
        {booking.categories.length > 0 && <span>{booking.categories.join(', ')}</span>}
        {booking.needs.length > 0 && (
          <span>
            · {money(booking.price)} booked · {money(booking.paid)} paid
          </span>
        )}
        {warning && <StatusBadge label={warning} color="red" />}
        <Link
          to={`/vendors/${booking.vendorId}`}
          className="ml-auto inline-flex items-center gap-1 min-h-11 sm:min-h-0 text-primary-600 hover:text-primary-700"
        >
          Vendor page <ExternalLink size={14} />
        </Link>
      </div>

      <TabNav
        ariaLabel="Vendor booking sections"
        items={[
          { id: 'details', label: 'Details' },
          { id: 'needs', label: 'Needs', badge: booking.needs.length || null },
          { id: 'contacts', label: 'Contacts', badge: booking.contacts.length || null },
          { id: 'files', label: 'Files', badge: booking.attachmentCount || null },
          { id: 'portal', label: 'Portal' },
        ]}
        activeId={tab}
        onSelect={(id) => onTabChange(id as BookingSheetTab)}
      />

      {/* Kept mounted on every tab, only hidden: the draft lives in it, and a
          look at the needs or files must not throw away what was typed. */}
      <div hidden={tab !== 'details'}>
        <BookingDetailsForm
          booking={booking}
          eventId={eventId}
          stages={stages}
          saving={patch.isPending}
          onSave={save}
          onUnsavedChange={onUnsavedChange}
        />
      </div>
      {tab === 'needs' && <BookingNeeds booking={booking} onOpenNeed={onOpenNeed} />}
      {tab === 'contacts' && <BookingContacts booking={booking} />}
      {tab === 'files' && (
        <EntityAttachmentSection
          entityType="ENTITY_LINK"
          entityId={booking.linkId}
          title="Contract, insurance and other files"
          requestFiles={{
            entityType: 'ENTITY_LINK',
            entityId: booking.linkId,
            recordName: booking.vendorName,
            suggestedRecipients: bookingFileRecipients(booking),
            folderScope: { entityType: 'Event', entityId: eventId },
          }}
        />
      )}
      {tab === 'portal' && <VendorPortalAccessPanel booking={booking} eventId={eventId} />}
    </div>
  );
}

interface DetailsState {
  stage: string | null;
  arrivalTime: string;
  loadInTime: string;
  dayOfContactUserId: string;
  contractSignedAt: string;
  contractExpiresAt: string;
  coiExpiresAt: string;
  description: string;
}

function detailsOf(b: EventBooking): DetailsState {
  const m = b.metadata;
  return {
    // The stage shown is the one resolved (a booking with none reads as booked);
    // it is only written when changed.
    stage: b.stage?.value ?? null,
    arrivalTime: m.arrivalTime ?? '',
    loadInTime: m.loadInTime ?? '',
    dayOfContactUserId: m.dayOfContactUserId ?? '',
    contractSignedAt: m.contractSignedAt ?? '',
    contractExpiresAt: m.contractExpiresAt ?? '',
    coiExpiresAt: m.coiExpiresAt ?? '',
    description: m.description ?? '',
  };
}

/** Only what changed, a cleared field as null — a patch never rewrites the rest. */
export function changedDetails(
  before: DetailsState,
  after: DetailsState,
): EventVendorMetadataInput {
  const out: Record<string, string | null> = {};
  for (const key of Object.keys(after) as (keyof DetailsState)[]) {
    if (before[key] === after[key]) continue;
    const value = after[key];
    out[key] = typeof value === 'string' ? value.trim() || null : value;
  }
  return out as EventVendorMetadataInput;
}

const inputClass =
  'w-full min-h-11 sm:min-h-0 px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

function BookingDetailsForm({
  booking,
  eventId,
  stages,
  saving,
  onSave,
  onUnsavedChange,
}: {
  booking: EventBooking;
  eventId: string;
  stages: readonly BookingStageOption[];
  saving: boolean;
  onSave: (changes: EventVendorMetadataInput) => Promise<boolean>;
  onUnsavedChange: (dirty: boolean) => void;
}) {
  const [saved, setSaved] = useState(() => detailsOf(booking));
  const [form, setForm] = useState(saved);
  const changes = changedDetails(saved, form);
  const dirty = Object.keys(changes).length > 0;
  const patch = usePatchBooking();

  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    onUnsavedChange(dirty);
  }, [dirty, onUnsavedChange]);

  // The server's copy is taken when it arrives, and only while nothing is
  // typed — a refetch (a file added, a grant switched) never throws away what
  // is being written. Keyed on the booking alone: a save making the form clean
  // must not put back the copy fetched before it.
  useEffect(() => {
    if (dirtyRef.current) return;
    const fresh = detailsOf(booking);
    setSaved(fresh);
    setForm(fresh);
  }, [booking]);

  const set = <K extends keyof DetailsState>(key: K, value: DetailsState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dirty) return;
    if (await onSave(changes)) {
      setSaved(form);
      onUnsavedChange(false);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-4">
      <SettingListSelect
        label="Stage"
        options={stages}
        value={form.stage}
        onChange={(v) => set('stage', v)}
        emptyMessage="No booking stages in Settings → App Settings"
        testId="booking-stage"
      />

      <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
          On the day
        </legend>
        <label className="block">
          <span className="block text-xs text-gray-500 mb-1">Arrives at</span>
          <input
            type="time"
            value={form.arrivalTime}
            onChange={(e) => set('arrivalTime', e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block">
          <span className="block text-xs text-gray-500 mb-1">Load-in from</span>
          <input
            type="time"
            value={form.loadInTime}
            onChange={(e) => set('loadInTime', e.target.value)}
            className={inputClass}
          />
        </label>
        <div className="sm:col-span-2">
          <span className="block text-xs text-gray-500 mb-1">Our day-of contact</span>
          <UserSearchableSelect
            value={form.dayOfContactUserId}
            onChange={(v) => set('dayOfContactUserId', typeof v === 'string' ? v : '')}
            placeholder={booking.dayOfContactName ?? 'Nobody chosen'}
            allowCreate={false}
          />
        </div>
        <BookingTimelineAction booking={booking} eventId={eventId} />
      </fieldset>

      <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
          Paperwork
        </legend>
        <label className="block">
          <span className="block text-xs text-gray-500 mb-1">Contract signed</span>
          <input
            type="date"
            value={form.contractSignedAt}
            onChange={(e) => set('contractSignedAt', e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block">
          <span className="block text-xs text-gray-500 mb-1">Contract expires</span>
          <input
            type="date"
            value={form.contractExpiresAt}
            onChange={(e) => set('contractExpiresAt', e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block">
          <span className="block text-xs text-gray-500 mb-1">Insurance (COI) expires</span>
          <input
            type="date"
            value={form.coiExpiresAt}
            onChange={(e) => set('coiExpiresAt', e.target.value)}
            className={inputClass}
          />
        </label>
      </fieldset>

      <label className="block">
        <span className="block text-xs text-gray-500 mb-1">Notes</span>
        <textarea
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Load-in by the side door, needs two outlets…"
          className={inputClass}
        />
      </label>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <VendorMessagingControl
            vendorName={booking.vendorName}
            metadata={booking.metadata as Record<string, unknown>}
            disabled={patch.isPending}
            onChange={(grant) => patch.mutate({ linkId: booking.linkId, patch: grant })}
          />
          <span>{booking.metadata.canMessage ? 'Messaging on' : 'Messaging off'}</span>
        </div>
        <button
          type="submit"
          disabled={!dirty || saving}
          className="min-h-11 sm:min-h-9 px-4 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

function BookingNeeds({
  booking,
  onOpenNeed,
}: {
  booking: EventBooking;
  onOpenNeed: (needId: string) => void;
}) {
  if (booking.needs.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        Nothing is booked with {booking.vendorName} on this event yet. Accept one of their quotes,
        or book a need directly, on the Plan.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-gray-100 border border-gray-200 rounded-xl">
      {booking.needs.map((need) => (
        <li key={need.id}>
          <button
            type="button"
            onClick={() => onOpenNeed(need.id)}
            className="w-full min-h-11 text-left px-4 py-3 hover:bg-gray-50 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1"
          >
            <span className="min-w-0">
              <span className="block font-medium text-gray-900 truncate">
                {need.itemName || 'Untitled need'}
              </span>
              <span className="block text-xs text-gray-500">{need.vendorCategoryName}</span>
            </span>
            <span className="text-sm text-gray-600">
              {money(need.price)} · {money(need.paid)} paid
              {need.balance > 0.005 && need.dueDate ? ` · due ${shortDay(need.dueDate)}` : ''}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function ContactLinks({
  email,
  phone,
  name,
}: {
  email: string | null;
  phone: string | null;
  name: string;
}) {
  const linkClass =
    'min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 inline-flex items-center justify-center rounded-lg text-primary-600 hover:bg-primary-50';
  return (
    <span className="flex items-center gap-1">
      {phone && (
        <>
          <a href={phoneHref('tel', phone)} className={linkClass} aria-label={`Call ${name}`}>
            <Phone size={16} />
          </a>
          <a href={phoneHref('sms', phone)} className={linkClass} aria-label={`Text ${name}`}>
            <MessageSquare size={16} />
          </a>
          {buildWhatsAppUrl(phone) && (
            <a
              href={buildWhatsAppUrl(phone)!}
              target="_blank"
              rel="noopener noreferrer"
              className={`${linkClass} text-green-600`}
              aria-label={`WhatsApp ${name}`}
            >
              <WhatsAppIcon className="h-4 w-4" />
            </a>
          )}
        </>
      )}
      {email && (
        <a href={`mailto:${email}`} className={linkClass} aria-label={`Email ${name}`}>
          <Mail size={16} />
        </a>
      )}
    </span>
  );
}

function BookingContacts({ booking }: { booking: EventBooking }) {
  const rows = [
    ...(booking.vendorEmail || booking.vendorPhone
      ? [
          {
            key: 'vendor',
            name: booking.vendorName,
            detail: [formatPhone(booking.vendorPhone), booking.vendorEmail]
              .filter(Boolean)
              .join(' · '),
            email: booking.vendorEmail,
            phone: booking.vendorPhone,
            primary: false,
          },
        ]
      : []),
    ...booking.contacts.map((c) => ({
      key: c.userId,
      name: c.name,
      detail: [c.role, formatPhone(c.phone), c.email].filter(Boolean).join(' · '),
      email: c.email,
      phone: c.phone,
      primary: c.isPrimary,
    })),
  ];
  return (
    <div className="space-y-3">
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">
          No phone, email or contacts on file for this vendor.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-xl">
          {rows.map((row) => (
            <li key={row.key} className="flex items-center justify-between gap-3 px-4 py-2">
              <span className="min-w-0">
                <span className="flex items-center gap-1 font-medium text-gray-900">
                  {row.name}
                  {row.primary && (
                    <Star
                      size={12}
                      className="text-amber-500 fill-amber-500"
                      aria-label="Primary contact"
                    />
                  )}
                </span>
                {row.detail && (
                  <span className="block text-xs text-gray-500 truncate">{row.detail}</span>
                )}
              </span>
              <ContactLinks email={row.email} phone={row.phone} name={row.name} />
            </li>
          ))}
        </ul>
      )}
      <Link
        to={`/vendors/${booking.vendorId}`}
        className="inline-flex items-center gap-1 min-h-11 sm:min-h-0 text-sm text-primary-600 hover:text-primary-700"
      >
        Manage {booking.vendorName}’s contacts <ExternalLink size={14} />
      </Link>
    </div>
  );
}
